'use client'

import { Dialog } from '@vinted/web-ui'
import { useContext, useEffect, useState, useRef } from 'react'

import {
  GetShippingLabelContext,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ErrorItem, useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  GenerateLabelModal,
  DropOffsPointType,
  clickEvent,
  viewEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useLoadGoogleApi } from '@marketplace-web/shipping/google-maps-feature'
import {
  getDefaultShippingAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'
import {
  FieldName as ShippingContactFieldName,
  PhoneRequirement,
  ShippingContactType,
} from '@marketplace-web/shipping/shipping-contact-data'

import BuyerAddress from './Components/BuyerAddress'
import Carrier from './Components/Carrier'
import DropOffPoints from './Components/DropOffPoints'
import PickupDate from './Components/PickupDate'
import SellerAddress from './Components/SellerAddress'
import GenerateShippingLabelShippingContact from './Components/GenerateShippingLabelShippingContact'
import ShippingMethod from './Components/ShippingMethod'
import DialogSubmit from './Components/DialogSubmit'
import DialogInfoBanner from './Components/DialogInfoBanner'
import DialogNavigation from './Components/DialogNavigation'
import {
  isDateObjectPresent,
  isDropOffMapEnabled,
  isPackageTypeLabelTypeFlow,
  isReturnShipment,
  isOldDropOffPointTypeFlow,
  isDropOffTypeFlow,
  isShippingContactDisabled,
  isDigitalLabelFlowEnabled,
  isHomePickupSelected,
  shouldShowPickupDateOld,
  shouldShowPickupDateNew,
  hasMultipleLabelTypesForSelectedDropOff,
} from '../../utils/generate-label'
import {
  incrementScreenLoadCounter,
  observePageLoadTime,
} from '../utils/generate-label-observability'

type Props = {
  isShippingLabelLoading: boolean
  isGetShippingLabelModalOpen: boolean
  onGenerateLabelClick: () => void
  onChooseDropOffPointOpen: () => void
}

const GenerateShippingLabelModal = ({
  isShippingLabelLoading,
  isGetShippingLabelModalOpen,
  onGenerateLabelClick,
  onChooseDropOffPointOpen,
}: Props) => {
  const pageLoadStartTime = useRef<number>(performance.now())
  const isLoadTimeObserved = useRef(false)

  const translate = useTranslate('conversation.get_shipping_label.modal')
  const translateShippingContact = useTranslate('shipping_contact')
  const { track } = useTracking()
  const { conversation } = useConversationContext()

  const {
    fetch: fetchDefaultShippingAddress,
    transformedData: defaultShippingAddress,
    isLoading: isDefaultShippingAddressLoading,
  } = useFetch(getDefaultShippingAddress, transformDefaultShippingAddress)

  const transactionId = conversation?.transaction?.id
  const userSide = conversation?.transaction?.userSide

  const {
    state: {
      collectionDates: { collectionDates, confirmedCollectionDate, clearSelectedCollection },
      dropOffTypeSelection: { clearDropOffTypeSelection, selectedDropOffTypeKey },
      labelTypeSelection: { clearLabelTypeSelection },
      activeModal: { setActiveModal },
      labelOptions: { labelOptions, labelOptionsUiState, labelOptionsRewrite },
      labelTypeSelection: { selectedLabelType },
      dropOffPointTypeSelection: { handleClearDropOffPointTypeSelection, selectedDropOffPointType },
    },
  } = useContext(GetShippingLabelContext)
  const { fetchNearbyDropOffPoints, clearNearbyDropOffPoints, selectedDropOffPoint } =
    useDropOffPointMapContext()

  const [pickUpValidation, setPickUpValidation] = useState<string>('')
  const [addressValidation, setAddressValidation] = useState<string>('')
  const [shippingContactFieldError, setShippingContactFieldError] = useState<ErrorItem | null>(null)

  const isGoogleApiLoaded = useLoadGoogleApi()

  const isHomeDeliveryPickupSelected = isHomePickupSelected(
    selectedDropOffPointType,
    selectedDropOffTypeKey,
  )
  const isPickupDateEnabled = shouldShowPickupDateOld(isHomeDeliveryPickupSelected, labelOptions)
  const { newFlow, newFlowDetails: shouldShowNewFlowDetails } = shouldShowPickupDateNew(
    isHomeDeliveryPickupSelected,
    labelOptionsRewrite,
  )
  const isPickpDateFlow =
    isPickupDateEnabled || isHomeDeliveryPickupSelected || newFlow || shouldShowNewFlowDetails

  useEffect(() => {
    if (isLoadTimeObserved.current) return

    const observeLoadEnd = (state: 'succeeded' | 'failed') => {
      const itemLoadTime = performance.now() - pageLoadStartTime.current

      observePageLoadTime(state, itemLoadTime)
      incrementScreenLoadCounter(state)

      isLoadTimeObserved.current = true
    }

    if (labelOptions || labelOptionsRewrite) {
      observeLoadEnd('succeeded')
    } else {
      observeLoadEnd('failed')
    }
  }, [labelOptions, labelOptionsRewrite])

  useEffect(() => {
    if (!isGetShippingLabelModalOpen) return

    if (labelOptionsRewrite) {
      if (labelOptionsRewrite.sender.phoneNumber) {
        setShippingContactFieldError(null)
      }

      return
    }

    if (labelOptions && labelOptions.sender.phoneNumber) {
      setShippingContactFieldError(null)
    }
  }, [isGetShippingLabelModalOpen, labelOptions, labelOptionsRewrite])

  useEffect(() => {
    if (!isGoogleApiLoaded || !defaultShippingAddress) return
    if (!isDropOffMapEnabled(labelOptionsRewrite)) return

    if (isGetShippingLabelModalOpen)
      fetchNearbyDropOffPoints({ address: defaultShippingAddress, selectedLabelType })
  }, [
    fetchNearbyDropOffPoints,
    defaultShippingAddress,
    isGetShippingLabelModalOpen,
    isGoogleApiLoaded,
    selectedLabelType,
    labelOptions,
    labelOptionsRewrite,
  ])

  useEffect(() => {
    if (isGetShippingLabelModalOpen) fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress, isGetShippingLabelModalOpen])

  useEffect(() => {
    if (!isGetShippingLabelModalOpen || !transactionId) return

    if (!isDropOffMapEnabled(labelOptionsRewrite)) return
    if (!selectedDropOffPoint) return

    const { distance, distanceUnit } = selectedDropOffPoint

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
      distance,
      distance_unit: distanceUnit,
    })

    track(
      viewEvent({
        screen: 'get_shipping_label',
        target: 'see_drop_off_points',
        targetDetails,
      }),
    )
  }, [
    track,
    transactionId,
    isGetShippingLabelModalOpen,
    selectedDropOffPoint,
    // if user changes address distance chnages, need to track again
    defaultShippingAddress,
    labelOptions,
    labelOptionsRewrite,
  ])

  const trackGenerateLabelClick = () => {
    let dropOffTypeKey

    if (selectedDropOffPointType) {
      dropOffTypeKey = selectedDropOffPointType === DropOffsPointType.Home ? 'home' : 'shop'
    }

    if (!dropOffTypeKey && selectedDropOffTypeKey) {
      dropOffTypeKey = selectedDropOffTypeKey
    }

    const targetDetails = {
      transaction_id: transactionId,
      dropoff_type_key: dropOffTypeKey,
    }

    track(
      clickEvent({
        target: 'confirm',
        screen: 'get_shipping_label',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackChooseDropOffPointClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'see_drop_off_points',
        screen: 'get_shipping_label',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const isSelectedCollectionDatesValid = () => {
    let isValid = true

    if (!isPickpDateFlow) return isValid

    const isDateAvailable = isDateObjectPresent(confirmedCollectionDate, collectionDates?.dates)

    if (!isDateAvailable) {
      clearSelectedCollection()

      setPickUpValidation(translate('drop_off_points.drop_off_date_validation'))

      isValid = false
    }

    return isValid
  }

  const handleValidation = () => {
    let isValid = true

    if (!defaultShippingAddress) {
      setAddressValidation(translate('address_field_validation'))

      isValid = false
    }

    if (isPickpDateFlow && !confirmedCollectionDate) {
      setPickUpValidation(translate('choose_drop_off_date.validation'))

      isValid = false
    }

    return isValid
  }

  const getIsShippingContactValid = () => {
    if (!userSide) return false

    const labelOptionsData = labelOptionsRewrite || labelOptions

    if (!labelOptionsData) return false

    const { sender } = labelOptionsData

    if (sender.phoneNumberRequirement === PhoneRequirement.Mandatory && !sender.phoneNumber) {
      setShippingContactFieldError({
        field: ShippingContactFieldName.ShippingContact,
        value: translateShippingContact(`${userSide}.validation.required`),
      })

      return false
    }

    if (isShippingContactDisabled(labelOptionsRewrite, labelOptions)) return true

    setShippingContactFieldError(null)

    return true
  }

  const handleConfirmationOpen = () => {
    if (!handleValidation()) return

    setActiveModal(GenerateLabelModal.ConfirmationModal)
  }

  const handleGenerateLabelClick = () => {
    const isHandleValidationValid = handleValidation()
    const isShippingContactValid = getIsShippingContactValid()

    if (!isHandleValidationValid || !isShippingContactValid) return

    if (!isSelectedCollectionDatesValid()) return

    trackGenerateLabelClick()
    onGenerateLabelClick()
  }

  const removePickUpError = () => {
    setPickUpValidation('')
  }

  const handleShippingContactModalOpen = () => {
    setActiveModal(GenerateLabelModal.ShippingContactModal)
  }

  const handleModalClose = () => {
    clearDropOffTypeSelection()

    if (confirmedCollectionDate) clearSelectedCollection()
    if (isOldDropOffPointTypeFlow(labelOptions)) handleClearDropOffPointTypeSelection()
    removePickUpError()
    setAddressValidation('')
    setShippingContactFieldError(null)

    clearNearbyDropOffPoints()

    clearLabelTypeSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  const handleReturnShipmentPreviousClick = () => {
    if (isOldDropOffPointTypeFlow(labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)
    }

    if (isPackageTypeLabelTypeFlow(labelOptionsRewrite)) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    if (isDropOffTypeFlow(labelOptionsRewrite, labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    if (isDigitalLabelFlowEnabled(labelOptions, labelOptionsRewrite)) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    return setActiveModal(GenerateLabelModal.ReturnTheOrder)
  }

  const handlePreviousClick = () => {
    if (isOldDropOffPointTypeFlow(labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)
    }

    if (
      labelOptionsRewrite &&
      selectedLabelType &&
      hasMultipleLabelTypesForSelectedDropOff(labelOptionsRewrite, selectedDropOffTypeKey)
    ) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    if (isDropOffTypeFlow(labelOptionsRewrite, labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    if (isReturnShipment(labelOptionsRewrite)) {
      return handleReturnShipmentPreviousClick()
    }

    if (isPackageTypeLabelTypeFlow(labelOptionsRewrite)) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
  }

  const handleAddressDetailsClick = () => {
    setAddressValidation('')
    setPickUpValidation('')

    setActiveModal(GenerateLabelModal.AddressModal)
  }

  const handleChoosePickUpDateOpen = () => {
    removePickUpError()

    setActiveModal(GenerateLabelModal.CourierPickUpDateSelectionModal)
  }

  const handleChooseDropOffPointOpen = () => {
    trackChooseDropOffPointClick()

    onChooseDropOffPointOpen()
  }

  const renderSellerAddress = () => {
    if (isDefaultShippingAddressLoading)
      return <ContentLoader testId="generate-shipping-address-loader" />

    return (
      <SellerAddress
        address={defaultShippingAddress || null}
        onAddressDetailsClick={handleAddressDetailsClick}
        error={addressValidation}
      />
    )
  }

  const renderModalContent = () => {
    if (labelOptionsUiState === UiState.Pending)
      return <ContentLoader testId="generate-shipping-label-loader" />

    return (
      <ScrollableArea>
        <DialogNavigation
          handlePreviousClick={handlePreviousClick}
          handleModalClose={handleModalClose}
        />
        <DialogInfoBanner transactionId={transactionId} />
        <Carrier userSide={userSide} />
        <ShippingMethod />
        <DropOffPoints
          onDropOffPointOpen={handleChooseDropOffPointOpen}
          address={defaultShippingAddress}
        />
        {renderSellerAddress()}
        <PickupDate
          address={defaultShippingAddress || null}
          pickUpValidation={pickUpValidation}
          onClick={handleChoosePickUpDateOpen}
        />
        <GenerateShippingLabelShippingContact
          transactionId={transactionId}
          userSide={userSide}
          error={shippingContactFieldError}
          type={ShippingContactType.ShippingLabel}
          onClick={handleShippingContactModalOpen}
        />
        <BuyerAddress />
        <DialogSubmit
          handleModalClose={handleModalClose}
          onConfirmationOpen={handleConfirmationOpen}
          isShippingLabelLoading={isShippingLabelLoading}
          onGenerateLabelClick={handleGenerateLabelClick}
        />
      </ScrollableArea>
    )
  }

  const renderGenerateShippingLabelModal = () => {
    return (
      <Dialog
        hasScrollableContent
        show={isGetShippingLabelModalOpen}
        className="u-flexbox"
        testId="generate-shipping-label-modal"
      >
        {renderModalContent()}
      </Dialog>
    )
  }

  return renderGenerateShippingLabelModal()
}

export default GenerateShippingLabelModal
